/**
 * Phase 17 Plan 17-02 — CreateKpiDialog
 *
 * Opens from the KPI tab's "Add KPI" button (or chained from KpiTemplatePickerDialog).
 * Uses react-hook-form + zod for validation.
 *
 * Props:
 *   open / onOpenChange — Dialog visibility.
 *   workOrderId         — The WO this KPI belongs to.
 *   workOrderMachines   — WO's active machines (used to seed the applicableMachines multi-select).
 *   initialValues       — Optional pre-populated values (from template copy path).
 *
 * On success: invalidates ['kpis', workOrderId] via useCreateKpiMutation + closes dialog.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCreateKpiMutation } from '@/hooks/useKpiQueries';
import { toast } from '@/lib/toast';
import { formatMachineName } from '@/lib/utils/machine-label';
import { KPI_UNITS, type CreateKpiBody, type KpiUnit } from '@/types/kpi';
import type { WorkOrderMachineItem } from '@/types/work-orders';

// ---------------------------------------------------------------------------
// Zod schema
// ---------------------------------------------------------------------------

const createKpiSchema = z
  .object({
    name: z.string().min(3, 'Name must be at least 3 characters').max(100, 'Name must be at most 100 characters'),
    unit: z.enum([
      'Nos', 'Meters', 'RunningMeters', 'Hours', 'Kilometers',
      'Liters', 'Kilograms', 'Units', 'Trips', 'Custom',
    ] as const),
    customUnitText: z.string().max(50, 'Custom unit must be at most 50 characters').optional(),
    description: z.string().max(500, 'Description must be at most 500 characters').optional(),
    targetPerShift: z.preprocess(
      (v) => (v === '' || v === undefined || v === null ? undefined : Number(v)),
      z.number().positive('Target must be positive').optional(),
    ),
    applicableMachineIds: z.array(z.number()).min(1, 'Select at least one machine').optional(),
  })
  .superRefine((data, ctx) => {
    if (data.unit === 'Custom') {
      if (data.customUnitText === undefined || data.customUnitText.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customUnitText'],
          message: 'Custom unit text is required when unit is Custom',
        });
      }
    } else {
      // customUnitText is forbidden for non-Custom units
      if (data.customUnitText !== undefined && data.customUnitText.trim() !== '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customUnitText'],
          message: 'Custom unit text is only allowed when unit is Custom',
        });
      }
    }
  });

type CreateKpiFormValues = z.infer<typeof createKpiSchema>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CreateKpiDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (v: boolean) => void;
  readonly workOrderId: number;
  readonly workOrderMachines: readonly WorkOrderMachineItem[];
  readonly initialValues?: Partial<CreateKpiBody> | undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreateKpiDialog({
  open,
  onOpenChange,
  workOrderId,
  workOrderMachines,
  initialValues,
}: CreateKpiDialogProps): JSX.Element {
  const mutation = useCreateKpiMutation();
  const [serverError, setServerError] = useState<string | null>(null);

  const defaultMachineIds = workOrderMachines.map((m) => m.machineId);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CreateKpiFormValues>({
    resolver: zodResolver(createKpiSchema),
    defaultValues: {
      name: initialValues?.name ?? '',
      unit: initialValues?.unit ?? 'Nos',
      customUnitText: initialValues?.customUnitText ?? '',
      description: initialValues?.description ?? '',
      targetPerShift: initialValues?.targetPerShift,
      applicableMachineIds: initialValues?.applicableMachineIds
        ? [...initialValues.applicableMachineIds]
        : defaultMachineIds,
    },
  });

  const watchedUnit = watch('unit');
  const watchedMachineIds = watch('applicableMachineIds') ?? defaultMachineIds;

  function handleClose(): void {
    setServerError(null);
    mutation.reset();
    reset();
    onOpenChange(false);
  }

  function onSubmit(values: CreateKpiFormValues): void {
    setServerError(null);
    const body: CreateKpiBody = {
      name: values.name,
      unit: values.unit,
      ...(values.unit === 'Custom' && values.customUnitText
        ? { customUnitText: values.customUnitText }
        : {}),
      ...(values.description && values.description.trim() !== ''
        ? { description: values.description }
        : {}),
      ...(values.targetPerShift !== undefined ? { targetPerShift: values.targetPerShift } : {}),
      ...(values.applicableMachineIds !== undefined && values.applicableMachineIds.length > 0
        ? { applicableMachineIds: values.applicableMachineIds }
        : {}),
    };
    mutation.mutate(
      { woId: workOrderId, body },
      {
        onSuccess: () => {
          toast.success('KPI created');
          handleClose();
        },
        onError: (err) => {
          const msg = err.detail ?? err.message;
          setServerError(msg);
          toast.error('Failed to create KPI', { description: msg });
        },
      },
    );
  }

  function toggleMachine(machineId: number, checked: boolean): void {
    const current = watchedMachineIds;
    const next = checked
      ? [...current, machineId]
      : current.filter((id) => id !== machineId);
    setValue('applicableMachineIds', next);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="create-kpi-dialog">
        <DialogHeader>
          <DialogTitle>Add KPI</DialogTitle>
          <DialogDescription>
            Create a new KPI for this Work Order.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1">
            <Label htmlFor="kpi-name">Name</Label>
            <Input
              id="kpi-name"
              data-testid="kpi-name-input"
              {...register('name')}
              placeholder="e.g. Throughput"
            />
            {errors.name && (
              <p className="text-sm text-destructive" data-testid="kpi-name-error">
                {errors.name.message}
              </p>
            )}
          </div>

          {/* Unit */}
          <div className="space-y-1">
            <Label htmlFor="kpi-unit">Unit</Label>
            <Select
              value={watchedUnit}
              onValueChange={(v) => { setValue('unit', v as KpiUnit); }}
            >
              <SelectTrigger id="kpi-unit" data-testid="kpi-unit-trigger">
                <SelectValue placeholder="Select unit…" />
              </SelectTrigger>
              <SelectContent>
                {KPI_UNITS.map((u) => (
                  <SelectItem key={u} value={u} data-testid={`kpi-unit-option-${u}`}>
                    {u === 'RunningMeters' ? 'Running Meters' : u}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.unit && (
              <p className="text-sm text-destructive" data-testid="kpi-unit-error">
                {errors.unit.message}
              </p>
            )}
          </div>

          {/* Custom unit text — only shown when unit=Custom */}
          {watchedUnit === 'Custom' && (
            <div className="space-y-1">
              <Label htmlFor="kpi-custom-unit">Custom Unit Text</Label>
              <Input
                id="kpi-custom-unit"
                data-testid="kpi-custom-unit-input"
                {...register('customUnitText')}
                placeholder="e.g. Pallets"
              />
              {errors.customUnitText && (
                <p className="text-sm text-destructive" data-testid="kpi-custom-unit-error">
                  {errors.customUnitText.message}
                </p>
              )}
            </div>
          )}

          {/* Description */}
          <div className="space-y-1">
            <Label htmlFor="kpi-description">Description (optional)</Label>
            <Textarea
              id="kpi-description"
              data-testid="kpi-description-input"
              {...register('description')}
              placeholder="What this KPI measures…"
              rows={2}
            />
          </div>

          {/* Target per shift */}
          <div className="space-y-1">
            <Label htmlFor="kpi-target">Target per Shift (optional)</Label>
            <Input
              id="kpi-target"
              data-testid="kpi-target-input"
              type="number"
              step="any"
              min="0"
              {...register('targetPerShift')}
              placeholder="e.g. 100"
            />
            {errors.targetPerShift && (
              <p className="text-sm text-destructive" data-testid="kpi-target-error">
                {errors.targetPerShift.message}
              </p>
            )}
          </div>

          {/* Applicable machines */}
          {workOrderMachines.length > 0 && (
            <div className="space-y-2">
              <Label>Applicable Machines</Label>
              <div className="space-y-1" data-testid="kpi-machines-checklist">
                {workOrderMachines.map((m) => (
                  <div key={m.machineId} className="flex items-center gap-2">
                    <Checkbox
                      id={`kpi-machine-${String(m.machineId)}`}
                      data-testid={`kpi-machine-checkbox-${String(m.machineId)}`}
                      checked={watchedMachineIds.includes(m.machineId)}
                      onCheckedChange={(checked) => {
                        toggleMachine(m.machineId, checked === true);
                      }}
                    />
                    <Label
                      htmlFor={`kpi-machine-${String(m.machineId)}`}
                      className="font-normal cursor-pointer"
                    >
                      {formatMachineName(m.machineName, m.vehicleNumber, m.machineCode)}
                    </Label>
                  </div>
                ))}
              </div>
              {errors.applicableMachineIds && (
                <p className="text-sm text-destructive" data-testid="kpi-machines-error">
                  {errors.applicableMachineIds.message}
                </p>
              )}
            </div>
          )}

          {/* Server error */}
          {serverError !== null && (
            <div
              className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="create-kpi-error"
            >
              {serverError}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={mutation.isPending}
              data-testid="create-kpi-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              data-testid="create-kpi-submit"
            >
              {mutation.isPending ? 'Creating…' : 'Create KPI'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
